import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TrackJS } from 'trackjs';

import Browser from '@wetransfer/concorde-browser';
import I18n from '@module/i18n';
import Analytics from '@module/analytics';
import { mapToSpaceship } from '@module/transfer/build';

import Actions from '@app/action';
import Config from '@app/config';
import FeatureFlagsHelper from '@app/helper/featureflags';

import Dropzone from '@component/dropzone';
import ItemList from '@component/itemlist';
import Icon from '@component/icon';
import { needsTips } from '@component/transfer';

export class Files extends React.Component {
  constructor(props) {
    super(props);

    this.onClick = this.onClick.bind(this);
    this.onFilesSelected = this.onFilesSelected.bind(this);
    this.onFilesFocus = this.onFilesFocus.bind(this);
    this.onFilesBlur = this.onFilesBlur.bind(this);
    this.onResetForward = this.onResetForward.bind(this);
    this.removeFile = this.removeFile.bind(this);
    this.showTip = this.showTip.bind(this);
    this.hideTip = this.hideTip.bind(this);
    this.showFeatureTip = this.showFeatureTip.bind(this);
  }

  // When this component gets unmounted we'll let the
  // "Dropzone" know that it's no longer possible to drop files.
  componentWillUnmount() {
    this.setDropzone(true);
  }

  // When this component gets mounted we'll let the
  // "Dropzone" know that it's possible to drop files now.
  componentDidMount() {
    this.setDropzone();

    if (this.props.fileCount) {
      this.scrollToEndList();
    }
  }

  // In some update scenarios we want to enforce specific behaviour
  componentDidUpdate(prevProps) {
    this.setDropzone();

    // If the fileCount is updated, we always show the 2 (and a half) last added files.
    if (this.node && prevProps.fileCount < this.props.fileCount) {
      this.scrollToEndList();
    }
  }

  setDropzone(forceRemoval) {
    // On mobile we don't provide a panel
    if (!this.props.panel) {
      return;
    }
    // When we're forwarding a transfer, the dropzone should be removed.
    if (forceRemoval || this.props.transfer.parent_id) {
      return Dropzone.removeTarget(this.props.panel);
    }

    return Dropzone.setTarget(this, this.props.panel);
  }

  scrollToEndList() {
    if (!this.node) {
      return;
    }

    const scrollTo = this.node.querySelector(
      '.filelist__item:nth-last-child(2)'
    );
    if (scrollTo) {
      const offset =
        this.props.fileCount > 2 && !isNaN(scrollTo.offsetHeight)
          ? -scrollTo.offsetHeight / 2
          : 0;
      // Wait for a paint to do scrolly stuff
      window.requestAnimationFrame(() =>
        this.props.panelComponent.scrollTo(scrollTo, { offset })
      );
    }
  }

  // This method is called when adding files, either via the
  // input field in this component OR from the Dropzone -
  // the reason that the Dropzone is also handled here is
  // that it gives us one place to manage error while adding
  // files to the list.
  addFiles(files = [], options = {}) {
    if (options.isSentFromIFrame && Browser.matches('chrome')) {
      return this.props.pushTransferNotification({
        text: 'DragAndDropFailed',
      });
    }
    if (options.isSentFromIFrame && !Browser.matches('chrome')) {
      // Manually track drag and drop attempt in a browser that this is not supported at the moment.
      // This will help us find out if this error is happening in other browsers as well.
      TrackJS.track('UnsupportedDragAndDropAttempt');
    }
    this.props.addFiles(files);
  }

  // Handle the event when files are selected on input[type=file]
  // so add the files to the transfer and reset the field to be used
  // again, for even more files!
  onFilesSelected(e) {
    if (!e.target.value) {
      return;
    } // empty string

    this.addFiles(e.target.files);
    this.formNode.reset(); // To reset file input's value (value is an empty string henceforth)
  }

  // Handle the focus/blur events of input[type=file]
  onFilesFocus() {
    if (!this.emptyStateNode) {
      return;
    }
    this.emptyStateNode.classList.add('uploader__empty-state--focused');
  }

  onFilesBlur() {
    if (!this.emptyStateNode) {
      return;
    }
    this.emptyStateNode.classList.remove('uploader__empty-state--focused');
  }

  // Handle the event when a file is clicked, asking the user to delete
  // this file from the transfer or not.
  removeFile(fileIndex) {
    const { transfer } = this.props;
    Analytics.pushDataLayerEvent({
      category: 'Transfer',
      action: 'File Removed',
      label: transfer.files[fileIndex].size,
    });
    this.props.removeFile(fileIndex);
  }

  // This should trigger a click on the input[type=file] field
  // so it opens the file dialog
  onClick(e) {
    e.stopPropagation();
    if (this.props.onClick) {
      this.props.onClick();
    }
    this.inputNode.click();
  }

  // Reset the forward
  onResetForward() {
    this.props.create();
  }

  showFeatureTip() {
    const {
      notification: { features },
      user,
    } = this.props;
    return (
      user.loggedIn &&
      FeatureFlagsHelper.unlimitedTransfers &&
      features.storage.supported &&
      !features.data.unlimitedTransfersUploader
    );
  }

  showTip() {
    let text;

    if (needsTips()) {
      text = 'TipFiles';
    } else if (this.showFeatureTip()) {
      // Does the user have the unlimited transfer feature?
      // Has the notification already been shown?
      text = 'UnlimitedSize';
    }

    if (text) {
      this.props.pushTransferNotification({
        text,
      });
    }
  }

  hideTip() {
    if (!needsTips() && !this.showFeatureTip()) {
      return;
    }
    this.props.clearTransferNotification();
  }

  // This render method is called if there are 0 files in the transfer
  renderEmptyState() {
    const { transfer, account } = this.props;
    const classNames = ['uploader__empty-state'];

    let fileSizeLimitText = (
      <p>
        {I18n.t('transfer.form.available_space', {
          size: I18n.size(transfer.availableSize, { precision: 0 }),
        })}
      </p>
    );

    let isUnlimitedStorage = false;
    if (account && typeof account.storage_limit === 'number') {
      const size = I18n.size(account.storage_limit);
      isUnlimitedStorage = size && size.includes('500');
    }

    if (transfer.isTransferSizeUnlimited || isUnlimitedStorage) {
      fileSizeLimitText = <p>{I18n.t('transfer.form.drop_files')}</p>;

      if (Browser.isMobile) {
        fileSizeLimitText = null;
      }
    }

    return (
      <div
        ref={(node) => (this.emptyStateNode = node)}
        className={classNames.join(' ')}
        onClick={this.onClick}
        onMouseEnter={this.showTip}
        onMouseLeave={this.hideTip}
      >
        <Icon type="TransferAddFirst" />
        <h2>{I18n.t('transfer.form.add_files')}</h2>
        {fileSizeLimitText}
      </div>
    );
  }

  // This render method is called if there are 1+ files in the transfer
  renderSummary(transfer) {
    let addMoreButton = (
      <Icon type="Add" color="#409FFF" className="uploader__add-files" />
    );
    let addMoreLabel = (
      <span className="uploader__add-files-label" onClick={this.onClick}>
        {I18n.t('transfer.form.more_files')}
      </span>
    );
    let onDelete = this.removeFile;
    let onClick = this.onClick;
    let precision = 1;
    let filesCountLabel = I18n.t('transfer.form.num_files', {
      remaining: I18n.size(transfer.availableSize, {
        precision,
      }),
      count: transfer.files.length,
    });

    // For transfers we don't show the 'add more files' button
    if (transfer.isForward) {
      addMoreButton = (
        <Icon
          type="Add"
          color="#409FFF"
          className="uploader__add-files uploader__add-files--reset"
          onClick={this.onResetForward}
        />
      );
      addMoreLabel = (
        <span
          className="uploader__add-files-label"
          onClick={this.onResetForward}
        >
          {I18n.t('transfer.form.reset_forward')}
        </span>
      );
      onDelete = null;
      onClick = null;
    }

    if (transfer.availableSize <= Config.transfer.precisionThreshold) {
      precision = 0;
    }

    if (transfer.isTransferSizeUnlimited) {
      filesCountLabel = I18n.t('transfer.form.files_count', {
        count: transfer.files.length,
        fileSize: I18n.size(transfer.totalSize, {
          precision,
        }),
      });
    }

    return (
      <div className="uploader__list">
        <ItemList transfer={transfer} onDelete={onDelete} />
        <div className="uploader__footer">
          <div className="uploader__add-more" onClick={onClick}>
            {addMoreButton}
            <div className="uploader__add-files-content">
              {addMoreLabel}
              <span className="uploader__add-files-count">
                {filesCountLabel}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render the list of files.
  render() {
    let { transfer } = this.props;

    // This is the only component where we directly call the mapToSpaceship method.
    // Thing is, is that our api still expects the file object, but all the child components expect items.
    transfer = mapToSpaceship(transfer);

    // What are we going to display?
    let component = null;
    if (!transfer.items.length) {
      component = this.renderEmptyState();
    } else {
      component = this.renderSummary(transfer);
    }

    return (
      <div ref={(node) => (this.node = node)} className="uploader__files">
        <form ref={(node) => (this.formNode = node)}>
          <input
            ref={(node) => (this.inputNode = node)}
            type="file"
            multiple
            onChange={this.onFilesSelected}
            onFocus={this.onFilesFocus}
            onBlur={this.onFilesBlur}
          />
        </form>
        {component}
      </div>
    );
  }
}

Files.propTypes = {
  addFiles: PropTypes.func.isRequired,
  clearTransferNotification: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  fileCount: PropTypes.number,
  notification: PropTypes.object,
  onClick: PropTypes.func,
  panel: PropTypes.object,
  panelComponent: PropTypes.object,
  pushTransferNotification: PropTypes.func.isRequired,
  removeFile: PropTypes.func.isRequired,
  transfer: PropTypes.object,
  user: PropTypes.object,
  account: PropTypes.object,
};

const mapStateToProps = ({ notification, transfer, user, account }) => ({
  notification,
  transfer,
  user,
  account,
});

const mapDispatchToProps = (dispatch) => ({
  addFiles: (files) => dispatch(Actions.Transfer.addFiles(files)),
  create: () => dispatch(Actions.Transfer.create()),
  removeFile: (fileIndex) => dispatch(Actions.Transfer.removeFile(fileIndex)),
  clearTransferNotification: () =>
    dispatch(Actions.Notification.clearTransfer()),
  pushTransferNotification: (notification) =>
    dispatch(Actions.Notification.pushTransfer(notification)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Files);
